import { useState } from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    triggerExtendedTranscription,
    fetchExtendedTranscriptionInfo,
} from 'flow/actions/transcription-actions';
import { TranscriptionExtendedResultCode } from 'flow/types/CallRecord';
import { CallRecordKey } from 'flow/types/EntityKeys';
import {
    selectCallRecordIsPersonalRecording,
    selectCallRecordQueueKey,
    selectCallRecordRecordedCallKey,
} from 'flow/selectors/call-records-selectors';
import { translationKeyByResultCode } from 'flow/components/modals/transcription/TranscriptionResultInfo';
import TranscriptionQuotaExceeded from 'flow/components/modals/transcription/TranscriptionQuotaExceeded';
import SkeletonSummary from './SkeletonSummary';

interface Props {
    resultCode: TranscriptionExtendedResultCode;
    callRecordKey: CallRecordKey;
}

const SectionTranscriptionError = ({ resultCode, callRecordKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const recordedCallKey = useAppSelector((state) =>
        selectCallRecordRecordedCallKey(state, callRecordKey),
    );
    const callRecordQueueKey = useAppSelector((state) =>
        selectCallRecordQueueKey(state, callRecordKey),
    );
    const isPersonalRecording = useAppSelector((state) =>
        selectCallRecordIsPersonalRecording(state, callRecordKey),
    );
    const [awaitingRetryConfirmation, setAwaitingRetryConfirmation] =
        useState(false);

    if (awaitingRetryConfirmation) {
        return <SkeletonSummary />;
    }

    if (resultCode === 'QUOTA_EXCEEDED') {
        return (
            <TranscriptionQuotaExceeded
                onRetry={
                    recordedCallKey
                        ? () => {
                              // Show skeleton while waiting for the server to accept or reject the retry.
                              // On success, fetch extendedTranscriptionInfo which starts the polling flow.
                              // Reset in .finally(). On failure, the warning reappears.
                              setAwaitingRetryConfirmation(true);
                              dispatch(
                                  triggerExtendedTranscription(
                                      recordedCallKey,
                                      isPersonalRecording,
                                  ),
                              )
                                  .then(() => {
                                      void dispatch(
                                          fetchExtendedTranscriptionInfo(
                                              callRecordKey,
                                              callRecordQueueKey,
                                          ),
                                      );
                                  })
                                  .finally(() =>
                                      setAwaitingRetryConfirmation(false),
                                  );
                          }
                        : undefined
                }
            />
        );
    }

    const errorMessage = translationKeyByResultCode[resultCode]
        ? i18next.t(translationKeyByResultCode[resultCode])
        : i18next.t('ai.summary.could_not_summarise');

    return (
        <div className="text-sm text-gray-400 italic antialiased">
            {errorMessage}
        </div>
    );
};

export default SectionTranscriptionError;
